import api from '../utils/api';
import { FileItem } from '../types/files';

export interface StorageStats {
  total: number;
  used: number;
  byType: {
    documents: number;
    images: number;
    videos: number;
    audio: number;
    archives: number;
  };
}

export interface DashboardStats {
  storage: StorageStats;
  recentFiles: FileItem[];
  favoriteFiles: FileItem[];
  recentFolders: {
    id: string;
    name: string;
    itemCount: number;
    lastModified: Date;
    path: string;
  }[];
  activitySummary: {
    totalActivities: number;
    countsByType: Record<string, number>;
    activityByDay: {
      date: string;
      count: number;
    }[];
    mostActiveResources: {
      resourceId: string;
      resourceType: string;
      resourceName: string;
      count: number;
      lastActivity: Date;
    }[];
  };
  securityAlerts: {
    id: number;
    severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
    title: string;
    message: string;
    timestamp: Date;
    actionLink?: string;
    actionText?: string;
    read?: boolean;
  }[];
}

const dashboardService = {
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },

  async getStorageStats(): Promise<StorageStats> {
    const response = await api.get('/dashboard/storage');
    return response.data;
  },

  async getRecentFiles(limit: number = 6): Promise<FileItem[]> {
    const response = await api.get(`/dashboard/recent-files?limit=${limit}`);
    return response.data.files;
  },

  async getFavoriteFiles(limit: number = 6): Promise<FileItem[]> {
    const response = await api.get(`/dashboard/favorite-files?limit=${limit}`);
    return response.data.files;
  },

  async getRecentFolders(limit: number = 4): Promise<DashboardStats['recentFolders']> {
    const response = await api.get(`/dashboard/recent-folders?limit=${limit}`);
    return response.data.folders;
  },

  async getActivitySummary(days: number = 7): Promise<DashboardStats['activitySummary']> {
    const response = await api.get(`/dashboard/activity-summary?days=${days}`);
    return response.data;
  },

  async getSecurityAlerts(): Promise<DashboardStats['securityAlerts']> {
    const response = await api.get('/dashboard/security-alerts');
    return response.data.alerts;
  },

  async dismissSecurityAlert(alertId: number): Promise<void> {
    await api.post(`/dashboard/security-alerts/${alertId}/dismiss`);
  },

  async markSecurityAlertAsRead(alertId: number): Promise<void> {
    await api.post(`/dashboard/security-alerts/${alertId}/read`);
  }
};

export default dashboardService; 